import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { UserType } from "@/types/user";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Car,
  HandCoins,
  Check,
  MessageCircle,
  AlertTriangle,
  Info,
  Loader2,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useValuation } from "@/hooks/useValuation";
import type { VehicleEstimationResponse } from "@/types/valuation";
import { useLanguage } from "@/context/LanguageContext";
import { listingPostLabels, valuationLabels, carDetailsLabels } from "@/i18n";
import type { PriceSectionVehicleInfo } from "@/utils/priceSectionVehicleInfo";
import { toVehicleEstimationRequest } from "@/utils/vehicleEstimationRequest";

interface PriceSectionProps {
  price: string;
  comments: string;
  vatCar: boolean;
  isEditMode?: boolean;
  isCompletionMode?: boolean;
  testStatus?: string; // Test status to determine if submit button should be shown
  /** Built by `toPriceSectionVehicleInfo` - never hand-assemble this literal. */
  vehicleInfo: PriceSectionVehicleInfo;
  onPriceChange: (price: string) => void;
  onCommentsChange: (comments: string) => void;
  onVatCarChange: (vatCar: boolean) => void;
  onSubmitForReview: () => void;
  onEstimationDataReceived?: (
    estimationData: VehicleEstimationResponse,
  ) => void;
  isLoading?: boolean;
  error?: string;
  successMessage?: string;
  /** If set, Momsbil toggle is only shown for dealer or business. Omit to show for all. */
  userType?: number; // UserType enum
}

export const PriceSection = ({
  price,
  comments,
  vatCar,
  isEditMode = false,
  isCompletionMode = false,
  testStatus,
  vehicleInfo,
  onPriceChange,
  onCommentsChange,
  onVatCarChange,
  onSubmitForReview,
  onEstimationDataReceived,
  isLoading = false,
  error,
  successMessage,
  userType,
}: PriceSectionProps) => {
  const { language } = useLanguage();
  const ps = (listingPostLabels(language).priceSection ?? {}) as Record<
    string,
    string
  >;
  const vLabels = (valuationLabels(language).expectations ?? {}) as Record<
    string,
    string
  >;
  const cdLabels = (carDetailsLabels(language).bidding ?? {}) as Record<
    string,
    string
  >;
  const showMomsbil =
    userType === UserType.Dealer ||
    userType === UserType.Business ||
    userType === UserType.Admin;
  const [priceWarning, setPriceWarning] = useState<{
    show: boolean;
    type: "low" | "high" | null;
    message: string;
  }>({ show: false, type: null, message: "" });
  const navigate = useNavigate();

  // Get vehicle estimation from AutoUncle
  const {
    getVehicleEstimationAsync,
    isGettingEstimation,
    estimationError,
    estimationData,
  } = useValuation();

  // Check if vehicle is a motorcycle (MC)
  const isMC = vehicleInfo.vehicleType === "mc";

  // Fetch vehicle estimation on mount if registrationDate is available
  // Skip for motorcycles (MC) - AutoUncle cannot valuate them
  useEffect(() => {
    if (
      !isMC &&
      !vehicleInfo.purchasePrice &&
      //vehicleInfo?.registrationDate &&
      vehicleInfo?.mileage !== undefined &&
      vehicleInfo?.regNumber &&
      vehicleInfo?.make &&
      vehicleInfo?.model
    ) {
      getVehicleEstimationAsync(toVehicleEstimationRequest(vehicleInfo)).catch(
        (error) => {},
      );
    }
  }, []); // Only run on mount

  // Call callback when estimationData is populated
  useEffect(() => {
    if (estimationData && onEstimationDataReceived) {
      onEstimationDataReceived(estimationData);
    }
  }, [estimationData, onEstimationDataReceived]);

  vehicleInfo.purchasePrice =
    estimationData?.purchasePrice ?? vehicleInfo?.purchasePrice;

  useEffect(() => {
    if (!price || price === "" || !vehicleInfo.purchasePrice) {
      setPriceWarning({ show: false, type: null, message: "" });
      return;
    }

    const priceNum = parseInt(price);
    if (isNaN(priceNum)) {
      setPriceWarning({ show: false, type: null, message: "" });
      return;
    }

    // Calculate percentage difference from estimated market value
    const percentageDiff =
      Math.abs(
        (priceNum - vehicleInfo.purchasePrice) / vehicleInfo.purchasePrice,
      ) * 100;

    // Show warning if price is more than 30% different from estimated value
    if (percentageDiff > 30) {
      // TODO: ÄR ALLT DETTA KORREKT
      if (priceNum < vehicleInfo.purchasePrice) {
        setPriceWarning({
          show: true,
          type: "low",
          message: `Är du säker på att du vill annonsera bilen för ${priceNum.toLocaleString()} kr? Det är långt under estimerat marknadsvärde!`,
        });
      } else {
        setPriceWarning({
          show: true,
          type: "high",
          message: `Är du säker på att du vill annonsera bilen för ${priceNum.toLocaleString()} kr? Det är långt över estimerat marknadsvärde!`,
        });
      }
    } else {
      setPriceWarning({ show: false, type: null, message: "" });
    }
  }, [price, vehicleInfo.purchasePrice]);

  // Determine if we should show error card
  const showEstimationError = estimationError || !vehicleInfo?.purchasePrice;
  const showEstimationCard = !showEstimationError && estimationData;

  return (
    <div className="w-full max-w-2xl mx-auto p-4 sm:p-8 bg-card border rounded-lg shadow space-y-4 sm:space-y-6">
      {isMC ? (
        // MC-specific UI - AutoUncle cannot valuate motorcycles
        <Card className="flex p-4 sm:p-6 flex-col border-2 border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20">
          <div className="text-center space-y-3 sm:space-y-4">
            <div className="flex justify-center">
              <Info className="h-8 w-8 sm:h-10 sm:w-10 text-green-600 dark:text-green-400" />
            </div>
            <div className="space-y-2">
              <h3 className="text-base sm:text-lg font-semibold text-green-900 dark:text-green-100">
                Värdering av din motorcykel
              </h3>
              <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 px-2 sm:px-4">
                Vi har nu all information vi behöver för att ge dig ett exakt
                pris. Skicka in annonsen så kontaktar vi dig inom kort för ett
                estimat.
              </p>
            </div>
          </div>
        </Card>
      ) : isGettingEstimation ? (
        <Card className="flex p-4 flex-col">
          <div className="flex justify-center items-center">
            <Loader2 className="h-8 w-8 animate-spin text-green-600" />
          </div>
        </Card>
      ) : showEstimationError ? (
        <Card className="flex p-4 sm:p-6 flex-col border-2 border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-950/20">
          <div className="text-center space-y-3 sm:space-y-4">
            <div className="flex justify-center">
              <AlertTriangle className="h-8 w-8 sm:h-10 sm:w-10 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="space-y-2">
              <h3 className="text-base sm:text-lg font-semibold text-orange-900 dark:text-orange-100">
                {vLabels.valuationErrorTitle ??
                  "Något gick fel vid den automatiska värderingen"}
              </h3>
              <p className="text-sm sm:text-base text-orange-800 dark:text-orange-200 px-2 sm:px-4">
                {vLabels.valuationErrorDescTest ??
                  "Tyvärr gick något fel vid den automatiska värderingen. Men du kan ändå skicka in detta fordonstest för granskning så återkommer vi med en värdering från vår sida."}
              </p>
            </div>
            <div className="pt-2">
              <Info className="h-5 w-5 text-orange-600 dark:text-orange-400 mx-auto" />
            </div>
          </div>
        </Card>
      ) : showEstimationCard ||
        (vehicleInfo.purchasePrice && vehicleInfo.purchasePrice > 0) ? (
        <Card className="flex p-4 flex-col">
          <div className="text-center">
            <h2 className="text-lg md:text-2xl font-bold text-green-600 mb-2">
              Automatiskt kalkylerad värdering
            </h2>
            <p className="text-primary/70">
              Baserat på liknande fordon i Sverige
            </p>
            <div className="text-sm flex justify-center gap-2 p-3 rounded-lg">
              <MessageCircle className="h-5 w-5 text-blue-500" />
              Detta är en automatisk värdering baserad på marknadsdata.
            </div>
          </div>

          <div>
            <Card className="flex p-4 border-dashed flex-col gap-4 mt-2">
              <div className="flex items-center gap-4">
                <Car className="h-6 w-6 text-primary/50" />
                <div>
                  <p className="text-sm md:text-lg font-medium">
                    {vehicleInfo?.year} {vehicleInfo?.make} {vehicleInfo?.model}
                  </p>
                  <p className="text-primary/70">
                    {vehicleInfo?.engine && vehicleInfo?.fuelType
                      ? `${vehicleInfo.engine}, ${vehicleInfo.fuelType}`
                      : "Baserat på märke, modell och årsmodell"}
                  </p>
                </div>
              </div>
              <div className="flex flex-row items-center gap-4">
                <HandCoins className="h-6 w-6 text-primary/50" />
                <div>
                  <p className="text-xl md:text-3xl font-bold text-accent">
                    {vehicleInfo.purchasePrice.toLocaleString()} kr
                  </p>
                  <div className="flex items-center gap-1">
                    <p className="text-primary/70">
                      {vLabels.estimatedValuation}
                    </p>
                  </div>
                </div>
              </div>

              {/* <div className="flex items-center gap-2 text-primary/70">
                <Check className="h-5 w-5 text-green-500" />
                <p>
                  Värdering baserad på 459 liknande fordon via AutoUncle API
                </p>
              </div> */}
            </Card>
          </div>
        </Card>
      ) : null}

      <div className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">
            {ps.desiredPriceLabel ?? "Reservationspris (kr)"}{" "}
            <span className="text-red-500">*</span>
          </label>
          <Input
            type="number"
            min="0"
            value={price}
            onChange={(e) => onPriceChange(e.target.value)}
            className="w-full"
            placeholder={
              ps.desiredPricePlaceholder ?? "Ange lägsta pris du vill sälja för"
            }
            required
          />
          <p className="mt-1.5 text-xs text-muted-foreground">
            {ps.desiredPriceInfo ??
              "Reservationspris är det lägsta belopp du är villig att sälja bilen för."}
          </p>

          {priceWarning.show && (
            <div
              className={`mt-2 p-3 rounded-lg border flex items-start gap-2 ${
                priceWarning.type === "low"
                  ? "bg-orange-50 border-orange-200 text-orange-800"
                  : "bg-blue-50 border-blue-200 text-blue-800"
              }`}
            >
              {priceWarning.type === "low" ? (
                <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
              ) : (
                <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              )}
              <p className="text-sm font-medium">{priceWarning.message}</p>
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {ps.extraComments ?? "Extra kommentarer"}
          </label>
          <Textarea
            value={comments}
            onChange={(e) => onCommentsChange(e.target.value)}
            className="w-full"
            placeholder={
              ps.extraCommentsPlaceholder ??
              "Eventuella kommentarer till annonsen"
            }
            rows={3}
          />
        </div>
        {showMomsbil && (
          <div className="flex items-center gap-2">
            <Switch
              id="vat-car"
              checked={vatCar}
              onCheckedChange={onVatCarChange}
            />
            <label htmlFor="vat-car" className="text-sm">
              {cdLabels.vatCar ?? "Momsbil"}
            </label>
          </div>
        )}
      </div>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          <div className="flex items-center gap-2">
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <p className="text-sm font-medium">{successMessage}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Show submit button if not in edit mode, or if in edit mode and test status is ONGOING or COMPLEMENT */}
      {(!isEditMode ||
        (isEditMode &&
          (testStatus === "ongoing" || testStatus === "complement"))) && (
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-6">
          <Button
            type="button"
            onClick={onSubmitForReview}
            disabled={!price || isLoading}
            className="w-full sm:w-auto"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {ps.submitting ?? "Skickar..."}
              </>
            ) : (
              (ps.submitForReview ?? "Skicka för granskning")
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
